import { inject, Injectable } from '@angular/core';
import {
  BasePageMetaResolver,
  CategoryPageMetaResolver,
  CmsService,
  Page,
  PageRobotsMeta,
  ProductSearchPage,
  ProductSearchService,
  TranslationService,
} from '@spartacus/core';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { CategoryService } from '../services/category.service';

@Injectable()
export class CustomCategoryPageMetaResolver extends CategoryPageMetaResolver {
  protected override productSearchService = inject(ProductSearchService);
  protected override cms = inject(CmsService);
  protected override translation = inject(TranslationService);
  protected override basePageMetaResolver = inject(BasePageMetaResolver);
  protected categoryService = inject(CategoryService);

  override resolveTitle() {
    return this.basePageMetaResolver.resolveTitle().pipe(
      switchMap((data: any) => {
        if (data == undefined) {
          return this.searchPage$.pipe(
            switchMap((page: any) => {
              if (page.breadcrumbs) {
                const title = page.breadcrumbs[0]?.facetValueName ? page.breadcrumbs[0]?.facetValueName : '';
                return this.translation.translate('pageMetaResolver.category.title', {
                  title: title,
                });
              } else if (page.title) {
                const title = page.title;
                return this.translation.translate('pageMetaResolver.category.title', {
                  title: title,
                });
              } else {
                return this.translation.translate('pageMetaResolver.category.title', {});
              }
            })
          );
        } else {
          return of(data);
        }
      })
    );
  }

  resolveDescription() {
    return this.basePageMetaResolver.resolveDescription().pipe(
      switchMap((data: any) => {
        if (data == undefined) {
          return this.searchPage$.pipe(
            switchMap((page: any) => {
              if (page.breadcrumbs) {
                const title = page.breadcrumbs[0]?.facetValueName ? page.breadcrumbs[0]?.facetValueName : '';
                return this.translation.translate('pageMetaResolver.category.descriptionListing', {
                  title: title,
                });
              } else if (page.title) {
                const title = page.title;
                return this.translation.translate('pageMetaResolver.category.description', {
                  title: title,
                });
              } else {
                return this.translation.translate('pageMetaResolver.category.description', {});
              }
            })
          );
        } else {
          return of(data);
        }
      })
    );
  }

  override resolveRobots(): Observable<PageRobotsMeta[]> {
    return this.searchPage$.pipe(
      switchMap((page: ProductSearchPage | Page) => {
        if (
          this.isProductSearchPage(page) &&
          ((page.breadcrumbs !== undefined && page.breadcrumbs.length > 4) ||
            !page.products ||
            page.products.length === 0)
        ) {
          return of([PageRobotsMeta.NOINDEX, PageRobotsMeta.NOFOLLOW]);
        }

        return super.resolveRobots();
      })
    );
  }

  isProductSearchPage(object: any): object is ProductSearchPage {
    return 'breadcrumbs' in object;
  }

  override resolveCanonicalUrl(): Observable<string> {
    return this.basePageMetaResolver.resolveCanonicalUrl().pipe(
      map((url) => {
        const parts = url.split('/');
        const baseUrl = parts.slice(0, 3).join('/');
        const category = parts.pop() ?? '';
        return { baseUrl, category };
      }),
      switchMap(({ baseUrl, category }) =>
        this.categoryService.getCategory(category).pipe(map((categoryData) => `${baseUrl}${categoryData?.url}`))
      )
    );
  }
}
